import { CHANGE_ORDER_STATUS_OPTIONS } from '../changeOrders';
import { toMs } from '../today/collections';
import type { ChangeOrderDoc, UnitDoc, UploadedDoc } from './useUnitPageData';

const SQFT_PER_SQMT = 10.764;
const DAY = 86400000;
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Unit areas in sq mt, total in sq ft, and super built-up sq ft, same maths as the classic page. */
export function unitAreas(unit: UnitDoc, projectMeta: Record<string, any> | null) {
  const wash = num(unit.exclusiveWashArea) ?? num(unit.areaOfExclusiveWash) ?? num(unit.area_of_exclusive_wash) ?? 0;
  const rows = [
    { label: 'Carpet', sqmt: num(unit.carpetArea) },
    { label: 'Exclusive balcony', sqmt: num(unit.exclusiveBalconyArea) },
    { label: 'Exclusive wash', sqmt: wash > 0 ? wash : null },
    { label: 'Open terrace', sqmt: num(unit.openTerraceArea) },
  ].filter((r) => r.sqmt != null && r.sqmt > 0) as Array<{ label: string; sqmt: number }>;
  const totalSqmt = rows.reduce((sum, r) => sum + r.sqmt, 0);
  const totalSqftRaw = totalSqmt * SQFT_PER_SQMT;
  const byType: Record<string, number> = {};
  Object.entries({ ...(projectMeta?.sbAreaPercentByUnitType || {}), ...(projectMeta?.sbAreaPercentByUsage || {}) }).forEach(([k, v]) => {
    const n = Number(v);
    if (Number.isFinite(n) && n >= 0) byType[k.trim()] = n;
  });
  const typeKey = [unit.usage, unit.blockType, unit.unitType, unit.type].map((v) => String(v || '').trim()).find(Boolean) || '';
  const sbPercent = typeKey && byType[typeKey] != null ? byType[typeKey] : Math.max(0, Number(projectMeta?.sbAreaPercent || 0) || 0);
  return {
    rows,
    totalSqmt,
    totalSqft: Math.ceil(totalSqftRaw),
    sbSqft: totalSqmt > 0 && sbPercent > 0 && sbPercent < 100 ? Math.ceil(totalSqftRaw / (1 - sbPercent / 100)) : null,
    sbPercent,
  };
}

const CLOSED_CO = new Set(['done', 'customer_confirmed', 'closed', 'rejected']);
const coStatus = (v: unknown) => {
  const raw = String(v || 'requested').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (raw === 'inprogress') return 'in_progress';
  if (raw === 'customerconfirmed') return 'customer_confirmed';
  if (raw === 'onhold') return 'on_hold';
  return CHANGE_ORDER_STATUS_OPTIONS.some((o) => o.value === raw) ? raw : 'requested';
};

export type ChangeOrderRow = { id: string; coNo: string; title: string; description: string; agency: string; status: string; statusLabel: string; open: boolean; waitingBuyer: boolean; waitingDays: number; costImpact: boolean; ms: number };

/** Change orders newest first; "waiting on buyer" = open, approval still pending. */
export function changeOrderRows(rows: ChangeOrderDoc[], now = new Date()): ChangeOrderRow[] {
  return rows
    .map((r) => {
      const status = coStatus(r.status);
      const open = !CLOSED_CO.has(status);
      const approval = String(r.customerApproval || 'pending').trim().toLowerCase();
      const ms = toMs(r.createdAt) || toMs(r.updatedAt) || 0;
      return {
        id: r.id,
        coNo: String(r.coNo || '').trim() || `CO-${r.id.slice(-6).toUpperCase()}`,
        title: String(r.title || r.category || '').trim() || 'Change request',
        description: String(r.description || '').trim(),
        agency: String(r.agency || '').trim(),
        status,
        statusLabel: CHANGE_ORDER_STATUS_OPTIONS.find((o) => o.value === status)?.label || status,
        open,
        waitingBuyer: open && approval === 'pending',
        waitingDays: ms ? Math.max(0, Math.floor((now.getTime() - ms) / DAY)) : 0,
        costImpact: !!r.costImpact,
        ms,
      };
    })
    .sort((a, b) => Number(b.open) - Number(a.open) || b.ms - a.ms);
}

const ACRONYMS = new Set(['SBI', 'NOC', 'GST', 'KYC', 'PAN', 'ICICI', 'HDFC', 'BUC', 'TDS', 'ITR', 'DP', 'BOB', 'PNB', 'LIC', 'POA']);
const titleCase = (s: string) =>
  s
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ')
    .map((w) => (ACRONYMS.has(w.toUpperCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join(' ');

export type DocSection = 'main' | 'loan' | 'draft';
export type DocRow = {
  id: string;
  label: string;
  files: Array<{ name: string; url: string }>;
  ms: number;
  draft: boolean;
  /** Where Document Config puts it: Booking (main), Loan or Drafts. */
  section: DocSection;
  /** Template it was generated from (can be regenerated); empty for scanned / uploaded files. */
  templateId: string;
  /** A signed copy linked to this draft has been uploaded (Document Config "Linked draft"). */
  replaced: boolean;
};
/** Document Config: upload types (officeDocumentUploadTypes) and generated templates (officeDocumentTemplates). */
export type DocConfig = { uploadTypes: Array<Record<string, any>>; templates: Array<Record<string, any>> };

const normKey = (v: unknown) => String(v || '').trim().toLowerCase();
const sectionFrom = (raw: unknown): DocSection | 'hidden' | '' => {
  const v = normKey(raw);
  return v === 'main_documents' ? 'main' : v === 'loan_documents' ? 'loan' : v === 'draft_documents' ? 'draft' : v === 'hidden' ? 'hidden' : '';
};

/** Booking documents with a readable name and their file links (no config: names from keys, all Booking/Drafts). */
export function documentRows(rows: UploadedDoc[], config?: DocConfig, fromDraftFolder = false): Array<DocRow & { hidden?: boolean }> {
  const typeByKey = new Map((config?.uploadTypes || []).map((t) => [normKey(t.key || t.id), t]));
  const tplById = new Map((config?.templates || []).map((t) => [normKey(t.templateId || t.id), t]));
  return rows
    .map((r) => {
      const raw: any = r;
      const key = String(r.type || r.templateId || r.id || '');
      const files = (r.files || []).filter((f) => f?.url).map((f) => ({ name: String(f.originalName || f.name || 'File'), url: String(f.url) }));
      const ms = Math.max(toMs(r.updatedAt) || 0, toMs(r.createdAt) || 0, ...(r.files || []).map((f) => Number(f?.uploadedAtMs || 0)));
      const tpl = tplById.get(normKey(r.templateId)) || tplById.get(normKey(r.id));
      const type = typeByKey.get(normKey(r.id)) || typeByKey.get(normKey(r.type));
      const templateId = String(r.templateId || (tpl ? tpl.templateId || tpl.id : '') || '').trim();
      // Same rule as the booking drawer: a template's "Show in", else final = Booking and draft = Drafts; upload types use their "Show in".
      const fromTpl = tpl ? sectionFrom(tpl.visualModel?.showInSection) || (normKey(tpl.visualModel?.generatedOutputType) === 'final' ? 'main' : 'draft') : '';
      const fromType = type ? sectionFrom(type.displaySection) || 'main' : '';
      const sec = fromTpl || fromType || (fromDraftFolder || /draft/i.test(key) ? 'draft' : 'main');
      const label = String(raw.displayName || type?.label || raw.templateName || tpl?.name || tpl?.title || '').trim() || titleCase(key.replace(/draft$/i, '')) || 'Document';
      return {
        id: r.id,
        label,
        files,
        ms,
        draft: sec === 'draft' || /draft/i.test(key),
        section: (sec === 'hidden' ? 'draft' : sec) as DocSection,
        hidden: sec === 'hidden',
        templateId,
        replaced: false,
      };
    })
    .sort((a, b) => b.ms - a.ms);
}

export function bookingDocumentRows(uploaded: UploadedDoc[], drafts: UploadedDoc[], config?: DocConfig): DocRow[] {
  const isPaymentDetails = (row: UploadedDoc) => ['payment_details_v1', 'payment_details_v2'].includes(String(row.templateId || row.id || '').trim());
  const current = [...uploaded, ...drafts].filter((row) => String(row.templateId || row.id || '').trim() === 'payment_details_v2');
  const paymentDetails = documentRows(current, config).find((row) => row.files.length > 0);
  const otherUploaded = documentRows(uploaded.filter((row) => !isPaymentDetails(row)), config);
  // Drafts from the separate draft folder, unless the same document is already in the uploaded list or has no file yet.
  const uploadedKeys = new Set(uploaded.map((row) => normKey(row.templateId || row.id)));
  const otherDrafts = config
    ? documentRows(drafts.filter((row) => !isPaymentDetails(row) && !uploadedKeys.has(normKey(row.templateId || row.id))), config, true).filter((row) => row.files.length > 0)
    : [];
  // A draft whose linked signed copy (Document Config "Linked draft") has been uploaded is marked replaced.
  const linked = new Set(
    (config?.uploadTypes || [])
      .filter((t) => uploaded.some((u) => normKey(u.id) === normKey(t.key || t.id) && (u.files || []).some((f) => f?.url)))
      .map((t) => normKey(t.linkedDraftTemplateId))
      .filter(Boolean)
  );
  const all = [...(paymentDetails ? [{ ...paymentDetails, label: 'Payment Details' }] : []), ...otherUploaded, ...otherDrafts]
    .filter((row) => !row.hidden)
    .map(({ hidden, ...row }) => ({ ...row, replaced: !!row.templateId && linked.has(normKey(row.templateId)) }));
  return all;
}
